import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPartner, setEditingPartner] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminAuth');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchData = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const [statsRes, dealersRes, partnersRes, activityRes] = await Promise.all([
        fetch(`${API_BASE}/admin/stats`, { headers }),
        fetch(`${API_BASE}/admin/dealers`, { headers }),
        fetch(`${API_BASE}/admin/partners`, { headers }),
        fetch(`${API_BASE}/admin/activity`, { headers })
      ]);

      if (!statsRes.ok) {
        localStorage.removeItem('adminAuth');
        navigate('/admin/login');
        return;
      }

      setStats(await statsRes.json());
      setDealers(await dealersRes.json());
      setPartners(await partnersRes.json());
      setActivity(await activityRes.json());
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateDealerStatus = async (dealerId, status) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const res = await fetch(`${API_BASE}/admin/dealers/${dealerId}/status?status=${status}`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      setError('Failed to update dealer status');
    }
  };

  const updatePartnerStatus = async (partnerId, status) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const res = await fetch(`${API_BASE}/admin/partners/${partnerId}/status?status=${status}`, {
        method: 'PUT',
        headers
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      setError('Failed to update partner status');
    }
  };

  const openEditPartner = async (partnerId) => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const res = await fetch(`${API_BASE}/admin/partners/${partnerId}`, { headers });
      if (res.ok) {
        const partnerData = await res.json();
        setEditingPartner(partnerData);
        setEditForm({
          company_name: partnerData.company_name || '',
          primary_contact_name: partnerData.primary_contact_name || '',
          primary_contact_email: partnerData.primary_contact_email || '',
          primary_contact_phone: partnerData.primary_contact_phone || '',
          street_address: partnerData.street_address || '',
          city: partnerData.city || '',
          state: partnerData.state || '',
          zip_code: partnerData.zip_code || '',
          website: partnerData.website || ''
        });
      }
    } catch (err) {
      setError('Failed to load partner details');
    }
  };

  const savePartner = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const res = await fetch(`${API_BASE}/admin/partners/${editingPartner.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setMessage('Partner updated successfully');
        setEditingPartner(null);
        fetchData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('Failed to update partner');
      }
    } catch (err) {
      setError('Failed to update partner');
    }
  };

  const resetPartnerPassword = async (partnerId) => {
    const headers = getAuthHeader();
    if (!headers) return;

    if (!window.confirm('Reset password for this partner? A new password will be generated.')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/admin/partners/${partnerId}/reset-password`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        const data = await res.json();
        alert(`Password reset for ${data.company_name}.\n\n${data.message}\n\nContact: ${data.email || 'N/A'}`);
      } else {
        setError('Failed to reset password');
      }
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const regeneratePartnerApiKey = async (partnerId) => {
    const headers = getAuthHeader();
    if (!headers) return;
    if (!window.confirm('Regenerate this partner\'s API key? The old key stops working immediately.')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/partners/${partnerId}/regenerate-api-key`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        alert(`New API key generated.\n\n${data.api_key || data.message || 'Key rotated.'}`);
        fetchData();
      } else {
        setError('Failed to regenerate API key');
      }
    } catch (err) {
      setError('Failed to regenerate API key');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div>
      <div className="main-content">
        <h2 style={{ marginTop: 0 }}>Dashboard</h2>
        {error && <div className="error">{error}</div>}
        {message && <div style={{ background: '#16a34a', padding: '10px', marginBottom: '20px', borderRadius: '4px', color: '#fff' }}>{message}</div>}

        <div className="tabs" style={{ marginBottom: '20px' }}>
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
            style={{ marginRight: '10px', padding: '10px 20px' }}
          >
            Overview
          </button>
          <button
            className={activeTab === 'dealers' ? 'active' : ''}
            onClick={() => setActiveTab('dealers')}
            style={{ marginRight: '10px', padding: '10px 20px' }}
          >
            Dealers ({dealers.length})
          </button>
          <button
            className={activeTab === 'partners' ? 'active' : ''}
            onClick={() => setActiveTab('partners')}
            style={{ marginRight: '10px', padding: '10px 20px' }}
          >
            Partners ({partners.length})
          </button>
          <button
            className={activeTab === 'activity' ? 'active' : ''}
            onClick={() => setActiveTab('activity')}
            style={{ marginRight: '10px', padding: '10px 20px' }}
          >
            Activity
          </button>
        </div>

        {activeTab === 'overview' && stats && (
          <div>
            <h2>Platform Statistics</h2>
            <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              <div className="stat-card" style={{ background: '#3b82f6', padding: '20px', borderRadius: '8px' }}>
                <h3>Dealers</h3>
                <p style={{ fontSize: '2em' }}>{stats.total_dealers}</p>
                <small>Active: {stats.active_dealers} | Pending: {stats.pending_dealers}</small>
              </div>
              <div className="stat-card" style={{ background: '#8b5cf6', padding: '20px', borderRadius: '8px' }}>
                <h3>Partners</h3>
                <p style={{ fontSize: '2em' }}>{stats.total_partners}</p>
                <small>Active: {stats.active_partners} | Pending: {stats.pending_partners}</small>
              </div>
              <div className="stat-card" style={{ background: '#22c55e', padding: '20px', borderRadius: '8px' }}>
                <h3>Subscriptions</h3>
                <p style={{ fontSize: '2em' }}>{stats.active_subscriptions}</p>
                <small>Active subscriptions</small>
              </div>
              <div className="stat-card" style={{ background: '#f59e0b', padding: '20px', borderRadius: '8px' }}>
                <h3>Vehicles</h3>
                <p style={{ fontSize: '2em' }}>{stats.total_vehicles}</p>
                <small>Total in platform</small>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'dealers' && (
          <div>
            <h2>Dealer Management</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#475569' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map(dealer => (
                  <tr key={dealer.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '12px' }}>{dealer.id}</td>
                    <td style={{ padding: '12px' }}>{dealer.dealership_name}</td>
                    <td style={{ padding: '12px' }}>{dealer.city}, {dealer.state}</td>
                    <td style={{ padding: '12px' }}>{dealer.primary_contact_email}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: dealer.account_status === 'active' ? '#22c55e' :
                                   dealer.account_status === 'pending' ? '#f59e0b' : '#ef4444'
                      }}>
                        {dealer.account_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {dealer.account_status === 'pending' && (
                        <button onClick={() => updateDealerStatus(dealer.id, 'active')}
                                style={{ marginRight: '5px', background: '#22c55e' }}>
                          Approve
                        </button>
                      )}
                      {dealer.account_status === 'active' && (
                        <button onClick={() => updateDealerStatus(dealer.id, 'suspended')}
                                style={{ background: '#ef4444' }}>
                          Suspend
                        </button>
                      )}
                      {dealer.account_status === 'suspended' && (
                        <button onClick={() => updateDealerStatus(dealer.id, 'active')}
                                style={{ background: '#22c55e' }}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'partners' && (
          <div>
            <h2>Partner Management</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#475569' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Company</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Credits</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map(partner => (
                  <tr key={partner.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '12px' }}>{partner.id}</td>
                    <td style={{ padding: '12px' }}>{partner.company_name}</td>
                    <td style={{ padding: '12px' }}>{partner.company_type}</td>
                    <td style={{ padding: '12px' }}>{partner.primary_contact_email}</td>
                    <td style={{ padding: '12px' }}>${partner.credit_balance}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: partner.account_status === 'active' ? '#22c55e' :
                                   partner.account_status === 'pending' ? '#f59e0b' : '#ef4444'
                      }}>
                        {partner.account_status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <button onClick={() => openEditPartner(partner.id)}
                              style={{ marginRight: '5px', background: '#3b82f6' }}>
                        Edit
                      </button>
                      <button onClick={() => resetPartnerPassword(partner.id)}
                              style={{ marginRight: '5px', background: '#f59e0b' }}>
                        Reset PW
                      </button>
                      <button onClick={() => regeneratePartnerApiKey(partner.id)}
                              style={{ marginRight: '5px', background: '#8b5cf6' }}>
                        Regen Key
                      </button>
                      {partner.account_status === 'pending' && (
                        <button onClick={() => updatePartnerStatus(partner.id, 'active')}
                                style={{ marginRight: '5px', background: '#22c55e' }}>
                          Approve
                        </button>
                      )}
                      {partner.account_status === 'active' && (
                        <button onClick={() => updatePartnerStatus(partner.id, 'suspended')}
                                style={{ background: '#ef4444' }}>
                          Suspend
                        </button>
                      )}
                      {partner.account_status === 'suspended' && (
                        <button onClick={() => updatePartnerStatus(partner.id, 'active')}
                                style={{ background: '#22c55e' }}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit Partner Modal */}
        {editingPartner && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#1e1e2e', padding: '30px', borderRadius: '8px',
              width: '500px', maxHeight: '80vh', overflow: 'auto'
            }}>
              <h2>Edit Partner: {editingPartner.company_name}</h2>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Company Name</label>
                <input type="text" value={editForm.company_name}
                       onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Contact Name</label>
                <input type="text" value={editForm.primary_contact_name}
                       onChange={(e) => setEditForm({...editForm, primary_contact_name: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Email</label>
                <input type="email" value={editForm.primary_contact_email}
                       onChange={(e) => setEditForm({...editForm, primary_contact_email: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Phone</label>
                <input type="text" value={editForm.primary_contact_phone}
                       onChange={(e) => setEditForm({...editForm, primary_contact_phone: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Address</label>
                <input type="text" value={editForm.street_address}
                       onChange={(e) => setEditForm({...editForm, street_address: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px' }}>City</label>
                  <input type="text" value={editForm.city}
                         onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                         style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px' }}>State</label>
                  <input type="text" value={editForm.state}
                         onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                         style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px' }}>ZIP</label>
                  <input type="text" value={editForm.zip_code}
                         onChange={(e) => setEditForm({...editForm, zip_code: e.target.value})}
                         style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
                </div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Website</label>
                <input type="text" value={editForm.website}
                       onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                       style={{ width: '100%', padding: '8px', borderRadius: '4px', background: '#2a2a3a', border: 'none', color: 'white' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setEditingPartner(null)} style={{ background: '#64748b' }}>Cancel</button>
                <button onClick={savePartner} style={{ background: '#22c55e' }}>Save Changes</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h2>Recent Activity</h2>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {activity.map((item, index) => {
                const isImport = item.type === 'import';
                const statusColor = (() => {
                  if (!isImport) return '#888';
                  if (item.status === 'completed') return '#4caf50';
                  if (item.status === 'completed_with_errors') return '#ff9800';
                  if (item.status === 'failed' || item.status === 'permanently_failed') return '#f44336';
                  return '#888';
                })();
                return (
                  <div key={index} style={{
                    padding: '12px',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>
                        <strong>{item.type.replace('_', ' ')}</strong>: {item.name}
                      </span>
                      <span style={{ color: '#888' }}>
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                    </div>
                    {isImport && (
                      <div style={{ fontSize: 13, color: '#bbb', paddingLeft: 8 }}>
                        <span style={{ color: statusColor, fontWeight: 600 }}>
                          {item.status}
                        </span>
                        {' — '}
                        found {item.records_found ?? 0}
                        {' · '}loaded {item.records_imported ?? 0}
                        {' · '}updated {item.records_updated ?? 0}
                        {' · '}removed {item.records_removed ?? 0}
                        {item.error_count > 0 && (
                          <span style={{ color: '#f44336' }}>
                            {' · '}errors {item.error_count}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
